import {
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
  useTransitionStyles,
} from "@floating-ui/react";
import { useRef, useState } from "react";

type TooltipSide = "bottom" | "left" | "right" | "top";

function getTooltipSide(placement: string): TooltipSide {
  if (placement.startsWith("bottom")) {
    return "bottom";
  }
  if (placement.startsWith("left")) {
    return "left";
  }
  if (placement.startsWith("right")) {
    return "right";
  }

  return "top";
}

/** Owns Floating UI interaction and runtime geometry for the rich input tooltip. */
function useInputTooltipFloatingImpl() {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef<HTMLDivElement>(null);
  const { refs, floatingStyles, placement, context } = useFloating({
    open: isOpen,
    placement: "top-start",
    // Floating UI reads this RefObject after the arrow element mounts.
    // oxlint-disable-next-line react-hooks/refs
    middleware: [offset(16), flip(), shift(), arrow({ element: arrowRef })],
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
  });
  const focusInteraction = useFocus(context);
  const dismissInteraction = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([focusInteraction, dismissInteraction]);
  const { isMounted } = useTransitionStyles(context, { duration: 150 });
  const side = getTooltipSide(placement);

  return {
    isOpen,
    isMounted,
    side,
    referenceRef: refs.setReference,
    getReferenceProps,
    floatingProps: {
      ref: refs.setFloating,
      style: floatingStyles,
      ...getFloatingProps(),
    },
    surfaceProps: {
      "data-state": isOpen ? "open" : "closed",
      "data-placement": side,
    },
    arrowProps: {
      ref: arrowRef,
      style: {
        top: side === "bottom" ? -4 : undefined,
        bottom: side === "top" ? -4 : undefined,
      },
    },
    close: () => setIsOpen(false),
  };
}

export type InputTooltipFloating = ReturnType<typeof useInputTooltipFloatingImpl>;

export function useInputTooltipFloating(): InputTooltipFloating {
  return useInputTooltipFloatingImpl();
}
